/**
 * AgentConfigTab.js — AI Agent Configuration & Simulation
 *
 * Lets users define AI agent configs (system prompt + KB) and run
 * simulations against imported Zendesk tickets.
 *
 * Also owns the "Global KB Configuration" section — a persistent inline
 * panel that lets users select KB articles and an agent prompt to apply
 * across all test runs (Zendesk Tickets tab, Benchmark tab). This replaces
 * the old KB nav item that opened a modal overlay.
 *
 * Props:
 *   theme               string   — 'dark' | 'light'
 *   triggerNewConfig    number   — increment to open new-config editor from header
 *   onActiveConfigChange fn(config) — lifts selected config to App.js
 *   onKbChange          fn({ kb_articles, agent_system_prompt }) — lifts global KB state to App.js
 *   sharedKbArticles    Array | null — current global KB selection from App.js
 *
 * API endpoints used:
 *   GET    /api/agent-configs
 *   POST   /api/agent-configs
 *   PUT    /api/agent-configs/:id
 *   DELETE /api/agent-configs/:id
 *   POST   /api/agent-configs/:id/duplicate
 *   POST   /api/agent-configs/:id/run
 *   GET    /api/zendesk/tickets
 *   GET    /api/kb/articles
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from './hooks/useApi';

// ── Palette (matches app dark theme) ─────────────────────────────────────────
const C = {
  bg:       '#0a0f1e',
  surface:  '#0f172a',
  elevated: '#141e33',
  border:   '#1e293b',
  borderHi: '#2d3f5e',
  purple:   '#7c3aed',
  purpleHi: '#9d5cf7',
  purpleLo: 'rgba(124,58,237,0.12)',
  text:     '#e2e8f0',
  muted:    '#64748b',
  green:    '#10b981',
  amber:    '#f59e0b',
  red:      '#ef4444',
  blue:     '#38bdf8',
};

// ── Score badge ───────────────────────────────────────────────────────────────
const ScoreBadge = ({ value, max = 5, label }) => {
  const pct = value / max;
  const color = pct >= 0.8 ? C.green : pct >= 0.6 ? C.amber : C.red;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <div style={{ fontSize: 18, fontWeight: 800, color }}>{value?.toFixed(1) ?? '—'}</div>
      <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
    </div>
  );
};

// ── Outcome pill ──────────────────────────────────────────────────────────────
const OutcomePill = ({ outcome }) => {
  const cfg = {
    resolved:   { color: C.green,  label: 'Resolved' },
    escalated:  { color: C.amber,  label: 'Escalated' },
    unresolved: { color: C.red,    label: 'Unresolved' },
  }[outcome] || { color: C.muted, label: outcome || '—' };
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 20,
      background: `${cfg.color}18`, color: cfg.color, border: `1px solid ${cfg.color}40` }}>
      {cfg.label}
    </span>
  );
};

// ── Empty state ───────────────────────────────────────────────────────────────
const EmptyState = ({ icon, title, subtitle, action }) => (
  <div style={{ textAlign: 'center', padding: '60px 20px' }}>
    <div style={{ fontSize: 44, marginBottom: 14 }}>{icon}</div>
    <div style={{ fontSize: 17, fontWeight: 700, color: C.text, marginBottom: 6 }}>{title}</div>
    <div style={{ fontSize: 13, color: C.muted, maxWidth: 360, margin: '0 auto 20px', lineHeight: 1.6 }}>{subtitle}</div>
    {action}
  </div>
);

// ── Live Endpoint Section (inside ConfigEditor) ───────────────────────────────
// Collapsible section for Level 2 live agent endpoint configuration.
// When endpoint_config is set on a saved config, the backend routes
// simulation runs through simulate_via_endpoint() instead of GPT-4.

// Per-format metadata: default auth type, default response path, hint copy
const FORMAT_META = {
  openai_compatible: {
    label: 'OpenAI-compatible',
    defaultAuth: 'bearer',
    defaultPath: 'choices[0].message.content',
    authHint: 'Authorization: Bearer <token>',
    hint: 'Standard /v1/chat/completions shape. Works with OpenAI, Azure OpenAI, and most hosted LLM APIs.',
    urlPlaceholder: 'https://api.openai.com/v1/chat/completions',
  },
  anthropic_compatible: {
    label: 'Anthropic-compatible',
    defaultAuth: 'api_key',
    defaultPath: 'content[0].text',
    authHint: 'x-api-key header',
    hint: 'Anthropic /v1/messages shape. Use your Anthropic API key as the Auth Value.',
    urlPlaceholder: 'https://api.anthropic.com/v1/messages',
  },
  zendesk_sunshine: {
    label: 'Zendesk Sunshine',
    defaultAuth: 'basic',
    defaultPath: 'responses[0].message.text',
    authHint: 'Basic auth — username:password',
    hint: 'Zendesk Sunshine Conversations API shape. Auth Value should be email:api_token.',
    urlPlaceholder: 'https://yoursubdomain.zendesk.com/api/v2/conversations',
  },
  custom: {
    label: 'Custom template',
    defaultAuth: 'none',
    defaultPath: '',
    authHint: 'Configure as required by your endpoint',
    hint: 'Define the full request body yourself. Use {{message}} as a placeholder for the customer message.',
    urlPlaceholder: 'https://your-agent.example.com/api/chat',
  },
};

const LiveEndpointSection = ({ configId, endpointConfig, onChange }) => {
  const { authFetch } = useApi();
  const [open, setOpen] = useState(!!endpointConfig);

  // Track whether the user has manually edited response_path so we don't
  // clobber their value when they switch format.
  const [pathEdited, setPathEdited] = useState(!!endpointConfig?.response_path);

  const [ep, setEp] = useState(endpointConfig || {
    url: '',
    auth_type: 'bearer',
    auth_value: '',
    request_format: 'openai_compatible',
    response_path: 'choices[0].message.content',
    timeout_seconds: 30,
    custom_template: '',
  });

  // Sync parent form.endpoint_config whenever ep changes (only when section is open)
  useEffect(() => {
    if (!open) return;
    const hasUrl = ep.url?.trim().length > 0;
    onChange(hasUrl ? { ...ep } : null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ep, open]);

  const setF = (key, val) => setEp(prev => ({ ...prev, [key]: val }));

  // When format changes: auto-set auth type and response path (if path not manually edited)
  const handleFormatChange = (newFormat) => {
    const meta = FORMAT_META[newFormat];
    setEp(prev => ({
      ...prev,
      request_format: newFormat,
      auth_type: meta.defaultAuth,
      response_path: pathEdited ? prev.response_path : meta.defaultPath,
    }));
  };

  const handlePathChange = (val) => {
    setPathEdited(true);
    setF('response_path', val);
  };

  // Test connection state
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const handleTestConnection = async () => {
    if (!configId) {
      setTestResult({ ok: false, message: 'Save the config first, then test the connection.' });
      return;
    }
    if (!ep.url?.trim()) {
      setTestResult({ ok: false, message: 'Enter an endpoint URL first.' });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await authFetch(`/api/agent-configs/${configId}/test-endpoint`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint_config: { ...ep } }),
      });
      setTestResult({ ok: true, message: res.reply, latency_ms: res.latency_ms });
    } catch (e) {
      setTestResult({ ok: false, message: e.message || 'Connection failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleToggle = () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (!nextOpen) onChange(null);
  };

  const meta = FORMAT_META[ep.request_format] || FORMAT_META.openai_compatible;

  const inputStyle = {
    width: '100%', padding: '9px 12px', borderRadius: 8,
    border: `1px solid ${C.border}`, background: C.elevated,
    color: C.text, fontSize: 13, outline: 'none',
    boxSizing: 'border-box', fontFamily: 'inherit',
  };
  const labelStyle = {
    fontSize: 11, fontWeight: 700, color: C.muted,
    letterSpacing: '0.06em', marginBottom: 6, display: 'block',
  };

  return (
    <div style={{ border: `1px solid ${open ? 'rgba(56,189,248,0.35)' : C.border}`, borderRadius: 10, overflow: 'hidden', transition: 'border-color 0.15s' }}>

      {/* Header toggle */}
      <div
        onClick={handleToggle}
        style={{ padding: '13px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: open ? 'rgba(56,189,248,0.05)' : 'transparent', userSelect: 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 16 }}>🔗</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
              Live Agent Endpoint
              <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 8, background: 'rgba(56,189,248,0.1)', color: C.blue, border: '1px solid rgba(56,189,248,0.25)' }}>
                Level 2
              </span>
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
              {open
                ? 'Point scenarios at your real deployed agent — bypasses GPT-4 simulation'
                : 'Optional · connect to a live agent API instead of GPT-4 simulation'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {endpointConfig?.url && !open && (
            <span style={{ fontSize: 11, color: C.blue }}>🔗 configured</span>
          )}
          <span style={{ color: C.muted, fontSize: 14 }}>{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Body */}
      {open && (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: '18px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* ── Step 1: Request Format (first — determines everything else) ── */}
          <div>
            <label style={labelStyle}>REQUEST FORMAT</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 10 }}>
              {Object.entries(FORMAT_META).map(([key, m]) => (
                <button
                  key={key}
                  onClick={() => handleFormatChange(key)}
                  style={{
                    padding: '8px 6px', borderRadius: 8, border: `1px solid ${ep.request_format === key ? 'rgba(56,189,248,0.6)' : C.border}`,
                    background: ep.request_format === key ? 'rgba(56,189,248,0.1)' : C.elevated,
                    color: ep.request_format === key ? C.blue : C.muted,
                    fontSize: 11, fontWeight: ep.request_format === key ? 700 : 400,
                    cursor: 'pointer', textAlign: 'center', lineHeight: 1.3,
                    transition: 'all 0.12s',
                  }}
                >
                  {m.label}
                </button>
              ))}
            </div>
            {/* Contextual hint for the selected format */}
            <div style={{ padding: '9px 12px', borderRadius: 7, background: 'rgba(56,189,248,0.05)', border: '1px solid rgba(56,189,248,0.15)', fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
              <span style={{ color: C.blue, fontWeight: 600 }}>Auth: </span>{meta.authHint}
              <span style={{ margin: '0 8px', color: C.border }}>·</span>
              {meta.hint}
            </div>
          </div>

          {/* ── Step 2: Endpoint URL ── */}
          <div>
            <label style={labelStyle}>ENDPOINT URL</label>
            <input
              value={ep.url}
              onChange={e => setF('url', e.target.value)}
              placeholder={meta.urlPlaceholder}
              style={inputStyle}
            />
          </div>

          {/* ── Step 3: Auth ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>AUTH TYPE</label>
              <select value={ep.auth_type} onChange={e => setF('auth_type', e.target.value)} style={inputStyle}>
                <option value="none">None</option>
                <option value="bearer">Bearer Token</option>
                <option value="api_key">API Key</option>
                <option value="basic">Basic Auth</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>
                AUTH VALUE{' '}
                {ep.auth_type === 'none'
                  ? <span style={{ fontWeight: 400 }}>(not required)</span>
                  : ep.auth_type === 'basic'
                  ? <span style={{ fontWeight: 400, color: C.muted }}>— user:password</span>
                  : ep.auth_type === 'api_key'
                  ? <span style={{ fontWeight: 400, color: C.muted }}>— API key value</span>
                  : <span style={{ fontWeight: 400, color: C.muted }}>— token (no "Bearer " prefix)</span>}
              </label>
              <input
                type="password"
                value={ep.auth_value}
                onChange={e => setF('auth_value', e.target.value)}
                placeholder={
                  ep.auth_type === 'none' ? '—'
                  : ep.auth_type === 'basic' ? 'username:password'
                  : ep.auth_type === 'api_key' ? 'sk-...'
                  : 'sk-...'
                }
                disabled={ep.auth_type === 'none'}
                style={{ ...inputStyle, opacity: ep.auth_type === 'none' ? 0.4 : 1, cursor: ep.auth_type === 'none' ? 'not-allowed' : 'text' }}
              />
            </div>
          </div>

          {/* ── Step 4: Response path ── */}
          <div>
            <label style={labelStyle}>RESPONSE PATH</label>
            <input
              value={ep.response_path}
              onChange={e => handlePathChange(e.target.value)}
              placeholder={meta.defaultPath || 'e.g. data.reply'}
              style={inputStyle}
            />
            <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
              Dot/bracket path to extract the agent reply from the JSON response.
              {meta.defaultPath && !pathEdited && (
                <span style={{ color: C.blue }}> Auto-set for {meta.label}.</span>
              )}
              {pathEdited && (
                <button
                  onClick={() => { setPathEdited(false); setF('response_path', meta.defaultPath); }}
                  style={{ marginLeft: 8, background: 'none', border: 'none', color: C.blue, fontSize: 10, cursor: 'pointer', padding: 0, fontWeight: 600 }}
                >
                  ↩ Reset to default
                </button>
              )}
            </div>
          </div>

          {/* Custom template — only when format = custom */}
          {ep.request_format === 'custom' && (
            <div>
              <label style={labelStyle}>CUSTOM REQUEST TEMPLATE</label>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
                Use <code style={{ color: C.blue }}>{'{{message}}'}</code> as a placeholder — replaced with the customer message at runtime.
              </div>
              <textarea
                value={ep.custom_template}
                onChange={e => setF('custom_template', e.target.value)}
                rows={5}
                placeholder={'{\n  "input": "{{message}}",\n  "session_id": "test-session"\n}'}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'ui-monospace, monospace', fontSize: 12, lineHeight: 1.6 }}
              />
            </div>
          )}

          {/* Timeout */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <label style={{ ...labelStyle, marginBottom: 0, whiteSpace: 'nowrap' }}>TIMEOUT (seconds)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
              <input type="range" min={5} max={120} value={ep.timeout_seconds}
                onChange={e => setF('timeout_seconds', parseInt(e.target.value))}
                style={{ flex: 1, accentColor: C.blue }} />
              <span style={{ fontSize: 14, fontWeight: 700, color: C.text, minWidth: 32 }}>{ep.timeout_seconds}s</span>
            </div>
          </div>

          {/* Test Connection */}
          <div style={{ paddingTop: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                onClick={handleTestConnection}
                disabled={testing || !ep.url?.trim()}
                style={{
                  padding: '8px 18px', borderRadius: 8, border: `1px solid ${C.blue}`,
                  background: testing ? 'transparent' : 'rgba(56,189,248,0.08)',
                  color: (testing || !ep.url?.trim()) ? C.muted : C.blue,
                  fontSize: 12, fontWeight: 600,
                  cursor: (testing || !ep.url?.trim()) ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                {testing
                  ? <><span style={{ display: 'inline-block', animation: 'spin 0.8s linear infinite' }}>⏳</span> Testing...</>
                  : '⚡ Test Connection'}
              </button>
              {!configId && (
                <span style={{ fontSize: 11, color: C.muted }}>Save this config first to enable connection testing</span>
              )}
            </div>

            {testResult && (
              <div style={{
                marginTop: 10, padding: '10px 14px', borderRadius: 8,
                background: testResult.ok ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
                border: `1px solid ${testResult.ok ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
                fontSize: 12,
              }}>
                {testResult.ok ? (
                  <div>
                    <div style={{ color: C.green, fontWeight: 700, marginBottom: 4 }}>
                      ✓ Agent replied{testResult.latency_ms ? ` in ${(testResult.latency_ms / 1000).toFixed(1)}s` : ''}
                    </div>
                    <div style={{ color: C.muted, fontStyle: 'italic', maxHeight: 60, overflow: 'hidden' }}>
                      "{testResult.message?.slice(0, 180)}{testResult.message?.length > 180 ? '…' : ''}"
                    </div>
                  </div>
                ) : (
                  <div style={{ color: C.red, fontWeight: 700 }}>✗ {testResult.message}</div>
                )}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

// ── Config card ───────────────────────────────────────────────────────────────
const ConfigCard = ({ config, selected, onSelect, onEdit, onDuplicate, onDelete, onRun, onSetActive, running }) => {
  const hasKb = config.knowledge_base?.trim().length > 0;
  const kbLines = config.knowledge_base?.trim().split('\n').length || 0;
  const isActive = config.is_active;
  return (
    <div
      onClick={() => onSelect(config.id)}
      style={{
        borderRadius: 12, padding: '16px 18px', cursor: 'pointer',
        border: `2px solid ${isActive ? C.green : selected ? C.purple : C.border}`,
        background: isActive ? 'rgba(16,185,129,0.05)' : selected ? C.purpleLo : C.surface,
        transition: 'border-color 0.15s, background 0.15s',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {config.name}
            </div>
            <span style={{ fontSize: 10, color: C.muted, background: C.elevated, padding: '1px 7px', borderRadius: 10, border: `1px solid ${C.border}`, flexShrink: 0 }}>
              v{config.version}
            </span>
            {isActive && (
              <span style={{
                fontSize: 10, fontWeight: 700, color: C.green,
                background: 'rgba(16,185,129,0.12)', padding: '1px 8px',
                borderRadius: 10, border: '1px solid rgba(16,185,129,0.3)', flexShrink: 0,
              }}>
                🟢 Active — used in all tests
              </span>
            )}
            {config.endpoint_config?.url && (
              <span style={{
                fontSize: 10, fontWeight: 700, color: C.blue,
                background: 'rgba(56,189,248,0.1)', padding: '1px 8px',
                borderRadius: 10, border: '1px solid rgba(56,189,248,0.25)', flexShrink: 0,
              }}>
                🔗 Live endpoint
              </span>
            )}
          </div>
          {config.name?.includes('(Copy)') && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 11, color: '#f59e0b', marginBottom: 6,
              background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)',
              borderRadius: 6, padding: '3px 10px',
            }}>
              ⚠️ Rename this config before using it in tests — "(Copy)" names make results hard to identify.
              <button
                onClick={e => { e.stopPropagation(); onEdit(config); }}
                style={{ background: 'transparent', border: 'none', color: '#f59e0b', fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
              >
                Rename now →
              </button>
            </div>
          )}
          {config.description && (
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {config.description}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: C.muted }}>🔄 {config.max_turns} turns</span>
            <span style={{ fontSize: 11, color: C.muted }}>🌡 temp {config.temperature}</span>
            {hasKb && <span style={{ fontSize: 11, color: C.blue }}>📚 KB ({kbLines} lines)</span>}
            {config.run_count > 0 && <span style={{ fontSize: 11, color: C.green }}>▶ {config.run_count} runs</span>}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          <button onClick={() => onRun(config)} disabled={running}
            title="Run simulation"
            style={{ padding: '5px 12px', borderRadius: 7, border: 'none', fontSize: 12, fontWeight: 600,
              cursor: running ? 'not-allowed' : 'pointer',
              background: running ? C.border : 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: 'white' }}>
            {running ? '⏳' : '▶ Run'}
          </button>
          {!isActive && onSetActive && (
            <button onClick={() => onSetActive(config.id)} title="Set as active agent for all tests"
              style={{ padding: '5px 10px', borderRadius: 7, border: `1px solid rgba(16,185,129,0.4)`, background: 'rgba(16,185,129,0.08)', color: C.green, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
              Set Active
            </button>
          )}
          <button onClick={() => onEdit(config)} title="Edit config"
            style={{ padding: '5px 10px', borderRadius: 7, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: 12, cursor: 'pointer' }}>
            ✏️ Edit
          </button>
          <button onClick={() => onDuplicate(config.id)} title="Duplicate for A/B testing"
            style={{ padding: '5px 10px', borderRadius: 7, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: 12, cursor: 'pointer' }}>
            ⎘ Copy
          </button>
          <button onClick={() => onDelete(config.id)} title="Delete config"
            style={{ padding: '5px 10px', borderRadius: 7, border: '1px solid rgba(239,68,68,0.3)', background: 'transparent', color: C.red, fontSize: 12, cursor: 'pointer' }}>
            🗑
          </button>
        </div>
      </div>
    </div>
  );
};
// ── Config editor modal ───────────────────────────────────────────────────────
const ConfigEditor = ({ config, onSave, onCancel }) => {
  const { authFetch } = useApi();
  const isNew = !config?.id;
  const existingEp = config?.endpoint_config || null;
  const [form, setForm] = useState({
    name: config?.name || '',
    description: config?.description || '',
    system_prompt: config?.system_prompt || 'You are a helpful customer support agent. Be concise, empathetic, and accurate.',
    knowledge_base: config?.knowledge_base || '',
    max_turns: config?.max_turns ?? 5,
    temperature: config?.temperature ?? 0.3,
    // Level 2 endpoint config (null = not set = Level 1 simulation)
    endpoint_config: existingEp,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // KB import from Zendesk
  const [kbLoading, setKbLoading] = useState(false);
  const [kbArticles, setKbArticles] = useState([]);
  const [kbPickerOpen, setKbPickerOpen] = useState(false);
  const [kbSelected, setKbSelected] = useState(new Set());
  const [kbError, setKbError] = useState('');

  const openKbPicker = async () => {
    setKbError('');
    setKbLoading(true);
    setKbPickerOpen(true);
    setKbArticles([]);
    try {
      const data = await authFetch('/api/kb/articles');
      setKbArticles(data.articles || []);
    } catch (e) {
      setKbError(e.message);
    } finally {
      setKbLoading(false);
    }
  };

  const toggleKbArticle = (id) => {
    setKbSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const applySelectedArticles = () => {
    const selected = kbArticles.filter(a => kbSelected.has(a.id));
    const text = selected.map(a => `## ${a.title}\n${a.body_text}`).join('\n\n---\n\n');
    setField('knowledge_base', form.knowledge_base.trim()
      ? `${form.knowledge_base.trim()}\n\n---\n\n${text}`
      : text
    );
    setKbPickerOpen(false);
    setKbSelected(new Set());
  };

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    if (!form.system_prompt.trim()) { setError('System prompt is required'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(form);
    } catch (e) {
      setError(e.message || 'Save failed');
      setSaving(false);
    }
  };

  const labelStyle = { fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.06em', marginBottom: 6, display: 'block' };
  const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.elevated, color: C.text, fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
  const textareaStyle = { ...inputStyle, resize: 'vertical', lineHeight: 1.6, fontFamily: 'ui-monospace, monospace', fontSize: 12 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => e.target === e.currentTarget && onCancel()}>
      <style>{`
        .config-editor-modal::-webkit-scrollbar { width: 8px; }
        .config-editor-modal::-webkit-scrollbar-track { background: transparent; }
        .config-editor-modal::-webkit-scrollbar-thumb { background: rgba(124,58,237,0.35); border-radius: 4px; }
        .config-editor-modal::-webkit-scrollbar-thumb:hover { background: rgba(124,58,237,0.6); }
        .config-editor-modal { scrollbar-width: thin; scrollbar-color: rgba(124,58,237,0.35) transparent; }
      `}</style>
      <div className="config-editor-modal" style={{ background: C.surface, border: `1px solid ${C.borderHi}`, borderRadius: 16, width: '100%', maxWidth: 680, maxHeight: '90vh', overflowY: 'scroll', padding: 28 }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{isNew ? '✨ New Agent Config' : '✏️ Edit Config'}</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Define how your AI agent behaves during simulation</div>
          </div>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 20, cursor: 'pointer', padding: '0 4px' }}>×</button>
        </div>

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: C.red, fontSize: 13 }}>
            ⚠️ {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Name + Description row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>CONFIG NAME *</label>
              <input value={form.name} onChange={e => setField('name', e.target.value)}
                placeholder="e.g. V1 - Standard Responses" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>DESCRIPTION</label>
              <input value={form.description} onChange={e => setField('description', e.target.value)}
                placeholder="What this config tests..." style={inputStyle} />
            </div>
          </div>

          {/* System Prompt */}
          <div>
            <label style={labelStyle}>SYSTEM PROMPT *</label>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
              This is the core instruction your AI agent receives. Define its persona, rules, and behavior.
            </div>
            <textarea
              value={form.system_prompt}
              onChange={e => setField('system_prompt', e.target.value)}
              rows={7}
              placeholder="You are a helpful customer support agent for [Company]. You should..."
              style={textareaStyle}
            />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
              {form.system_prompt.length} chars · ~{Math.ceil(form.system_prompt.length / 4)} tokens
            </div>
          </div>

          {/* Knowledge Base */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>
                KNOWLEDGE BASE <span style={{ color: C.muted, fontWeight: 400 }}>(optional)</span>
              </label>
              <button
                onClick={openKbPicker}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '4px 12px', borderRadius: 6, border: `1px solid ${C.borderHi}`,
                  background: C.elevated, color: C.blue, fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                📥 Import from Zendesk
              </button>
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
              Paste KB articles manually, or import directly from your connected Zendesk Help Center.
            </div>
            <textarea
              value={form.knowledge_base}
              onChange={e => setField('knowledge_base', e.target.value)}
              rows={8}
              placeholder={`## Refund Policy\nRefunds are available within 30 days of purchase...\n\n## Password Reset\nTo reset your password, visit settings > security...`}
              style={textareaStyle}
            />
            {form.knowledge_base.trim() && (
              <div style={{ fontSize: 11, color: C.blue, marginTop: 4 }}>
                📚 {form.knowledge_base.trim().split('\n').length} lines · ~{Math.ceil(form.knowledge_base.length / 4)} tokens
              </div>
            )}
          </div>

          {/* KB Article Picker (nested modal) */}
          {kbPickerOpen && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
              <div style={{ background: C.surface, border: `1px solid ${C.borderHi}`, borderRadius: 14, width: '100%', maxWidth: 580, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>📚 Zendesk Help Center Articles</div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Select articles to import into the knowledge base</div>
                  </div>
                  <button onClick={() => setKbPickerOpen(false)} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 20, cursor: 'pointer' }}>×</button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
                  {kbLoading && <div style={{ textAlign: 'center', padding: 40, color: C.muted }}><div style={{ fontSize: 24, marginBottom: 10 }}>⏳</div>Fetching articles from Zendesk Help Center...</div>}
                  {kbError && (
                    <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '12px 14px', color: C.red, fontSize: 13 }}>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠️ Could not fetch articles</div>
                      <div>{kbError}</div>
                    </div>
                  )}
                  {!kbLoading && !kbError && kbArticles.length === 0 && (
                    <div style={{ textAlign: 'center', padding: 40, color: C.muted, fontSize: 13 }}>No articles found in your Help Center.</div>
                  )}
                  {!kbLoading && kbArticles.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>
                        {kbArticles.length} article{kbArticles.length !== 1 ? 's' : ''} found · {kbSelected.size} selected
                        {kbArticles.length > 1 && (
                          <button
                            onClick={() => setKbSelected(kbSelected.size === kbArticles.length ? new Set() : new Set(kbArticles.map(a => a.id)))}
                            style={{ marginLeft: 10, cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 6, border: `1px solid ${C.purple}`, background: kbSelected.size === kbArticles.length ? C.purple : 'transparent', color: kbSelected.size === kbArticles.length ? 'white' : C.purpleHi }}>
                            {kbSelected.size === kbArticles.length ? '✓ Deselect all' : 'Select all'}
                          </button>
                        )}
                      </div>
                      {kbArticles.map(article => {
                        const sel = kbSelected.has(article.id);
                        return (
                          <div key={article.id} onClick={() => toggleKbArticle(article.id)}
                            style={{ padding: '10px 12px', borderRadius: 8, cursor: 'pointer', border: `1px solid ${sel ? C.purple : C.border}`, background: sel ? C.purpleLo : C.elevated, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                            <div style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0, marginTop: 1, border: `2px solid ${sel ? C.purple : C.border}`, background: sel ? C.purple : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {sel && <span style={{ color: 'white', fontSize: 10, fontWeight: 900 }}>✓</span>}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 2 }}>{article.title}</div>
                              <div style={{ fontSize: 11, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{article.body_text.slice(0, 100)}…</div>
                              {article.labels?.length > 0 && (
                                <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                                  {article.labels.map(l => <span key={l} style={{ fontSize: 9, padding: '1px 6px', borderRadius: 10, background: C.purpleLo, color: C.purpleHi, border: `1px solid rgba(124,58,237,0.2)` }}>{l}</span>)}
                                </div>
                              )}
                            </div>
                            <div style={{ fontSize: 10, color: C.muted, flexShrink: 0, marginTop: 2 }}>~{Math.ceil(article.body_text.length / 4)} tok</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {!kbLoading && !kbError && kbArticles.length > 0 && (
                  <div style={{ padding: '12px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 11, color: C.muted }}>
                      {kbSelected.size > 0 ? `~${kbArticles.filter(a => kbSelected.has(a.id)).reduce((s, a) => s + Math.ceil(a.body_text.length / 4), 0).toLocaleString()} tokens selected` : 'Select articles above'}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => setKbPickerOpen(false)} style={{ padding: '7px 16px', borderRadius: 7, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                      <button onClick={applySelectedArticles} disabled={kbSelected.size === 0}
                        style={{ padding: '7px 16px', borderRadius: 7, border: 'none', fontSize: 12, fontWeight: 600, cursor: kbSelected.size > 0 ? 'pointer' : 'not-allowed', background: kbSelected.size > 0 ? 'linear-gradient(135deg,#7c3aed,#4f46e5)' : C.border, color: 'white' }}>
                        Import {kbSelected.size > 0 ? `${kbSelected.size} article${kbSelected.size !== 1 ? 's' : ''}` : 'Articles'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Settings row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>MAX CONVERSATION TURNS</label>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>How many back-and-forth exchanges before marking unresolved</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="range" min={2} max={10} value={form.max_turns} onChange={e => setField('max_turns', parseInt(e.target.value))} style={{ flex: 1, accentColor: C.purple }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: C.text, minWidth: 20 }}>{form.max_turns}</span>
              </div>
            </div>
            <div>
              <label style={labelStyle}>TEMPERATURE</label>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>Lower = more consistent, higher = more creative responses</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="range" min={0} max={10} value={Math.round(form.temperature * 10)} onChange={e => setField('temperature', parseFloat((e.target.value / 10).toFixed(1)))} style={{ flex: 1, accentColor: C.purple }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: C.text, minWidth: 24 }}>{form.temperature}</span>
              </div>
            </div>
          </div>

          {/* ── Level 2: Live Agent Endpoint ── */}
          <LiveEndpointSection
            configId={config?.id || null}
            endpointConfig={form.endpoint_config}
            onChange={(ep) => setField('endpoint_config', ep)}
          />

        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24, paddingTop: 18, borderTop: `1px solid ${C.border}` }}>
          <button onClick={onCancel} style={{ padding: '9px 20px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '9px 24px', borderRadius: 8, border: 'none', background: saving ? C.border : 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: 'white', fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? 'Saving...' : isNew ? '✨ Create Config' : '💾 Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Run settings modal ────────────────────────────────────────────────────────
const RunSettingsModal = ({ config, tickets, loadingTickets, onRun, onCancel }) => {
  const [maxTickets, setMaxTickets] = useState(10);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [mode, setMode] = useState('sample');

  const toggleTicket = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleRun = () => {
    const ticket_ids = mode === 'select' ? [...selectedIds] : null;
    onRun({ ticket_ids, max_tickets: mode === 'sample' ? maxTickets : undefined });
  };

  const canRun = mode === 'sample' || selectedIds.size > 0;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => e.target === e.currentTarget && onCancel()}>
      <style>{`
        .run-modal::-webkit-scrollbar { width: 8px; }
        .run-modal::-webkit-scrollbar-track { background: transparent; }
        .run-modal::-webkit-scrollbar-thumb { background: rgba(124,58,237,0.35); border-radius: 4px; }
        .run-modal::-webkit-scrollbar-thumb:hover { background: rgba(124,58,237,0.6); }
        .run-modal { scrollbar-width: thin; scrollbar-color: rgba(124,58,237,0.35) transparent; }
      `}</style>
      <div className="run-modal" style={{ background: C.surface, border: `1px solid ${C.borderHi}`, borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '85vh', overflowY: 'scroll', padding: 26 }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>▶ Run Simulation</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Using config: <span style={{ color: C.purpleHi }}>{config.name}</span></div>
        </div>
        <div style={{ display: 'flex', border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 18 }}>
          {[['sample', '🎲 Random Sample'], ['select', '🎯 Pick Tickets']].map(([m, l]) => (
            <button key={m} onClick={() => setMode(m)}
              style={{ flex: 1, padding: '8px 0', border: 'none', background: mode === m ? C.purple : 'transparent', color: mode === m ? 'white' : C.muted, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {l}
            </button>
          ))}
        </div>
        {mode === 'sample' ? (
          <div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>Number of tickets to simulate (random sample from imported tickets)</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input type="range" min={1} max={Math.min(50, tickets.length || 50)} value={maxTickets} onChange={e => setMaxTickets(parseInt(e.target.value))} style={{ flex: 1, accentColor: C.purple }} />
              <span style={{ fontSize: 16, fontWeight: 800, color: C.text, minWidth: 36 }}>{maxTickets}</span>
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>~{Math.ceil(maxTickets * 2.5)} API calls · est. {Math.ceil(maxTickets * 4)}–{Math.ceil(maxTickets * 8)}s</div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>Select specific tickets ({selectedIds.size} selected)</div>
            {loadingTickets ? (
              <div style={{ textAlign: 'center', padding: 24, color: C.muted, fontSize: 13 }}>⏳ Loading tickets...</div>
            ) : tickets.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: C.muted, fontSize: 13 }}>No tickets imported yet. Go to the Zendesk Tickets tab first.</div>
            ) : (
              <div style={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {tickets.slice(0, 50).map(t => {
                  const id = String(t.zendesk_ticket_id || t.id);
                  const sel = selectedIds.has(id);
                  return (
                    <div key={id} onClick={() => toggleTicket(id)}
                      style={{ padding: '10px 12px', borderRadius: 8, cursor: 'pointer', border: `1px solid ${sel ? C.purple : C.border}`, background: sel ? C.purpleLo : C.elevated, display: 'flex', gap: 10, alignItems: 'center' }}>
                      <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${sel ? C.purple : C.border}`, background: sel ? C.purple : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {sel && <span style={{ color: 'white', fontSize: 10, fontWeight: 900 }}>✓</span>}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</div>
                        <div style={{ fontSize: 10, color: C.muted }}>#{id} · {t.priority} · {t.status}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
          <button onClick={onCancel} style={{ padding: '9px 20px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleRun} disabled={!canRun}
            style={{ padding: '9px 24px', borderRadius: 8, border: 'none', background: canRun ? 'linear-gradient(135deg,#7c3aed,#4f46e5)' : C.border, color: 'white', fontSize: 13, fontWeight: 600, cursor: canRun ? 'pointer' : 'not-allowed' }}>
            ▶ Start Simulation
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Simulation results panel ──────────────────────────────────────────────────
const SimulationResults = ({ data, configName, onClose }) => {
  const [expanded, setExpanded] = useState(null);
  const { summary, results } = data;

  const statCard = (label, value, color = C.text) => (
    <div style={{ background: C.elevated, borderRadius: 10, padding: '14px 18px', border: `1px solid ${C.border}`, textAlign: 'center' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
    </div>
  );

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.borderHi}`, borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(90deg, rgba(124,58,237,0.08), transparent)' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>📊 Simulation Results</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 1 }}>Config: <span style={{ color: C.purpleHi }}>{configName}</span></div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 18, cursor: 'pointer' }}>×</button>
      </div>
      <div style={{ padding: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
          {statCard('Containment Rate', `${summary.containment_rate}%`, summary.containment_rate >= 70 ? C.green : summary.containment_rate >= 50 ? C.amber : C.red)}
          {statCard('Pass Rate', `${summary.pass_rate}%`, summary.pass_rate >= 70 ? C.green : summary.pass_rate >= 50 ? C.amber : C.red)}
          {statCard('Tickets Tested', summary.total_tickets, C.blue)}
          {statCard('Avg Overall', `${summary.avg_scores.overall}/5`, summary.avg_scores.overall >= 4 ? C.green : summary.avg_scores.overall >= 3 ? C.amber : C.red)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20, padding: '14px 18px', background: C.elevated, borderRadius: 10, border: `1px solid ${C.border}` }}>
          {['accuracy', 'tone', 'resolution', 'efficiency'].map(m => <ScoreBadge key={m} value={summary.avg_scores[m]} label={m} />)}
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {Object.entries(summary.outcome_breakdown).map(([outcome, count]) => (
            <div key={outcome} style={{ flex: 1, minWidth: 100, padding: '10px 14px', borderRadius: 8, background: C.elevated, border: `1px solid ${C.border}`, textAlign: 'center' }}>
              <OutcomePill outcome={outcome} />
              <div style={{ fontSize: 20, fontWeight: 800, color: C.text, marginTop: 6 }}>{count}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, letterSpacing: '0.06em', marginBottom: 10 }}>PER-TICKET RESULTS</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {results.map((r, i) => (
            <div key={i} style={{ borderRadius: 10, border: `1px solid ${C.border}`, overflow: 'hidden', background: C.elevated }}>
              <div onClick={() => setExpanded(expanded === i ? null : i)} style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.subject}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>#{r.ticket_id} · {r.priority} · {r.status}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                  {r.run_status === 'error' ? (
                    <span style={{ fontSize: 12, color: C.red }}>⚠ Error</span>
                  ) : (
                    <>
                      <OutcomePill outcome={r.simulation?.outcome} />
                      <span style={{ fontSize: 13, fontWeight: 700, color: (r.evaluation?.overall_score / 5) >= 0.8 ? C.green : (r.evaluation?.overall_score / 5) >= 0.6 ? C.amber : C.red }}>
                        {r.evaluation?.overall_score ?? '—'}/100
                      </span>
                    </>
                  )}
                  <span style={{ color: C.muted, fontSize: 12 }}>{expanded === i ? '▲' : '▼'}</span>
                </div>
              </div>
              {expanded === i && r.run_status !== 'error' && (
                <div style={{ borderTop: `1px solid ${C.border}`, padding: '14px 16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 14 }}>
                    {['accuracy','tone','resolution','efficiency'].map(m => (
                      <div key={m} style={{ textAlign: 'center', padding: '8px', background: C.surface, borderRadius: 8 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: (r.evaluation?.[m]||0) >= 4 ? C.green : (r.evaluation?.[m]||0) >= 3 ? C.amber : C.red }}>{r.evaluation?.[m]?.toFixed(1) ?? '—'}/5</div>
                        <div style={{ fontSize: 10, color: C.muted, textTransform: 'capitalize' }}>{m}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.06em', marginBottom: 8 }}>CONVERSATION</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflowY: 'auto' }}>
                    {(r.simulation?.turns || []).map((turn, ti) => (
                      <div key={ti} style={{ display: 'flex', flexDirection: 'column', alignItems: turn.role === 'customer' ? 'flex-start' : 'flex-end' }}>
                        <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>{turn.role === 'customer' ? '👤 Customer' : '🤖 AI Agent'}</div>
                        <div style={{ maxWidth: '85%', padding: '8px 12px', borderRadius: turn.role === 'customer' ? '4px 10px 10px 10px' : '10px 4px 10px 10px', background: turn.role === 'customer' ? C.elevated : 'rgba(124,58,237,0.12)', border: `1px solid ${turn.role === 'customer' ? C.border : 'rgba(124,58,237,0.3)'}`, fontSize: 12, color: C.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {turn.content}
                        </div>
                      </div>
                    ))}
                  </div>
                  {r.evaluation?.accuracy_reasoning && (
                    <div style={{ marginTop: 12, padding: '10px 14px', background: C.surface, borderRadius: 8, border: `1px solid ${C.border}` }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 6 }}>AI JUDGE REASONING</div>
                      {['accuracy','tone','resolution','efficiency'].map(m => r.evaluation?.[`${m}_reasoning`] && (
                        <div key={m} style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>
                          <span style={{ color: C.text, fontWeight: 600, textTransform: 'capitalize' }}>{m}:</span> {r.evaluation[`${m}_reasoning`]}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {expanded === i && r.run_status === 'error' && (
                <div style={{ borderTop: `1px solid ${C.border}`, padding: '12px 16px', color: C.red, fontSize: 12 }}>Error: {r.error}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Global KB Section ─────────────────────────────────────────────────────────
// Inline section that replaces the old KB nav modal.
// Collapsible panel for selecting KB articles + optional agent prompt
// that apply across the Zendesk Tickets and Benchmark tabs.
// Includes KB Collections: named saved subsets of articles.
const GlobalKbSection = ({ onKbChange, sharedKbArticles }) => {
  const { authFetch } = useApi();
  const [isExpanded, setIsExpanded] = useState(false);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [selectedIds, setSelectedIds] = useState(() =>
    sharedKbArticles ? new Set(sharedKbArticles.map(a => String(a.id))) : new Set()
  );
  const [agentPrompt, setAgentPrompt] = useState('');
  const [saved, setSaved] = useState(false);

  // ── Collections state ──────────────────────────────────────────
  const [collections, setCollections] = useState([]);
  const [newCollName, setNewCollName] = useState('');
  const [savingColl, setSavingColl] = useState(false);
  const [showNewColl, setShowNewColl] = useState(false);

  const activeCount = sharedKbArticles ? sharedKbArticles.length : 0;

  const loadArticles = useCallback(async () => {
    if (articles.length > 0) return;
    setLoading(true);
    setLoadError('');
    try {
      const [artData, collData] = await Promise.all([
        authFetch('/api/kb/articles'),
        authFetch('/api/kb/collections'),
      ]);
      const normalized = (artData.articles || []).map(a => ({ ...a, body: a.body_text || a.body || '' }));
      setArticles(normalized);
      setCollections(collData.collections || []);
    } catch (e) {
      setLoadError(e.message || 'Could not load KB articles');
    } finally {
      setLoading(false);
    }
  }, [authFetch, articles.length]);

  const handleExpand = () => {
    if (!isExpanded) loadArticles();
    setIsExpanded(v => !v);
  };

  const toggleArticle = (id) => {
    const sid = String(id);
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(sid) ? next.delete(sid) : next.add(sid);
      return next;
    });
  };

  const applyCollection = (coll) => {
    setSelectedIds(new Set((coll.article_ids || []).map(String)));
  };

  const saveAsCollection = async () => {
    if (!newCollName.trim() || selectedIds.size === 0) return;
    setSavingColl(true);
    try {
      const data = await authFetch('/api/kb/collections', {
        method: 'POST',
        body: JSON.stringify({ name: newCollName.trim(), article_ids: [...selectedIds].map(String) }),
      });
      setCollections(prev => [...prev, data.collection]);
      setNewCollName('');
      setShowNewColl(false);
    } catch (e) {
      alert('Could not save collection: ' + (e.message || 'Unknown error'));
    } finally {
      setSavingColl(false);
    }
  };

  const deleteCollection = async (collId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this collection?')) return;
    try {
      await authFetch(`/api/kb/collections/${collId}`, { method: 'DELETE' });
      setCollections(prev => prev.filter(c => c.id !== collId));
    } catch {}
  };

  const handleSave = () => {
    const selected = articles.filter(a => selectedIds.has(String(a.id)));
    onKbChange({
      kb_articles: selected.length > 0 ? selected : null,
      agent_system_prompt: agentPrompt.trim() || null,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleClear = () => {
    setSelectedIds(new Set());
    setAgentPrompt('');
    onKbChange({ kb_articles: null, agent_system_prompt: null });
  };

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', marginTop: 24 }}>

      {/* ── Header (always visible, click to expand) ── */}
      <div onClick={handleExpand} style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0, background: activeCount > 0 ? 'rgba(124,58,237,0.15)' : C.elevated, border: `1px solid ${activeCount > 0 ? 'rgba(124,58,237,0.35)' : C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17 }}>
            📚
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Global KB Configuration</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 1 }}>
              {activeCount > 0
                ? `${activeCount} article${activeCount !== 1 ? 's' : ''} active — grounding Zendesk Tickets and Benchmark tab runs`
                : 'Choose which Help Center articles ground your agent across all test tabs · click to expand'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {activeCount > 0 && (
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: 'rgba(124,58,237,0.15)', color: C.purpleHi, border: '1px solid rgba(124,58,237,0.3)' }}>
              {activeCount} active
            </span>
          )}
          <span style={{ color: C.muted, fontSize: 13 }}>{isExpanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* ── Expanded body ── */}
      {isExpanded && (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: '20px 20px 24px' }}>
          <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.7, margin: '0 0 18px 0' }}>
            Select which Help Center articles the AI agent should reference on the{' '}
            <strong style={{ color: C.text }}>Zendesk Tickets</strong> and{' '}
            <strong style={{ color: C.text }}>Benchmark</strong> tabs. When articles are selected, a{' '}
            <strong style={{ color: C.purpleHi }}>Groundedness</strong> metric appears in results — scoring
            whether the agent cited the KB or hallucinated.
          </p>

          {/* ── Collections picker ── */}
          {!loading && (collections.length > 0 || articles.length > 0) && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.06em' }}>
                  📁 COLLECTIONS {collections.length > 0 && <span style={{ fontWeight: 400 }}>({collections.length})</span>}
                </span>
                <button
                  onClick={() => setShowNewColl(v => !v)}
                  disabled={selectedIds.size === 0}
                  title={selectedIds.size === 0 ? 'Select articles first, then save as a collection' : 'Save current selection as a named collection'}
                  style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: selectedIds.size > 0 ? C.purpleHi : C.muted, cursor: selectedIds.size > 0 ? 'pointer' : 'not-allowed' }}
                >
                  + Save as collection
                </button>
              </div>

              {showNewColl && (
                <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                  <input
                    value={newCollName}
                    onChange={e => setNewCollName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && saveAsCollection()}
                    placeholder="e.g. Billing Articles"
                    autoFocus
                    style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.purple}`, background: C.elevated, color: C.text, fontSize: 12, outline: 'none' }}
                  />
                  <button onClick={saveAsCollection} disabled={!newCollName.trim() || savingColl}
                    style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: C.purple, color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    {savingColl ? '...' : 'Save'}
                  </button>
                  <button onClick={() => setShowNewColl(false)}
                    style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: 12, cursor: 'pointer' }}>
                    ✕
                  </button>
                </div>
              )}

              {collections.length === 0 ? (
                <div style={{ fontSize: 12, color: C.muted, padding: '8px 12px', borderRadius: 6, border: `1px dashed ${C.border}`, textAlign: 'center' }}>
                  No collections yet — select articles below and save them as a named collection.
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {collections.map(coll => (
                    <button key={coll.id} onClick={() => applyCollection(coll)}
                      title={`${coll.article_ids.length} article${coll.article_ids.length !== 1 ? 's' : ''} — click to select`}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', border: `1px solid ${C.border}`, background: C.elevated, color: C.text, transition: 'all 0.12s' }}
                    >
                      📁 {coll.name}
                      <span style={{ fontSize: 10, color: C.muted, background: C.surface, borderRadius: 10, padding: '1px 6px', border: `1px solid ${C.border}` }}>
                        {coll.article_ids.length}
                      </span>
                      <span onClick={(e) => deleteCollection(coll.id, e)}
                        title="Delete collection"
                        style={{ fontSize: 11, color: C.muted, marginLeft: 2, cursor: 'pointer', lineHeight: 1 }}>✕</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {loading && <div style={{ textAlign: 'center', padding: '28px 0', color: C.muted, fontSize: 13 }}>⏳ Loading KB articles...</div>}

          {loadError && (
            <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '12px 14px', color: C.red, fontSize: 13, marginBottom: 16 }}>
              ⚠️ {loadError}
            </div>
          )}

          {!loading && !loadError && articles.length === 0 && (
            <div style={{ textAlign: 'center', padding: '28px 0', color: C.muted, fontSize: 13 }}>
              No KB articles imported yet. Import articles from your Zendesk Help Center first.
            </div>
          )}

          {!loading && articles.length > 0 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, padding: '8px 12px', background: C.elevated, borderRadius: 8, border: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>
                  {selectedIds.size} of {articles.length} articles selected
                  {selectedIds.size > 0 && (
                    <span style={{ fontWeight: 400, color: C.green, marginLeft: 8 }}>· Groundedness enabled</span>
                  )}
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setSelectedIds(new Set(articles.map(a => String(a.id))))}
                    style={{ fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 6, border: `1px solid ${C.purple}`, background: 'transparent', color: C.purpleHi, cursor: 'pointer' }}>
                    Select all
                  </button>
                  <button onClick={() => setSelectedIds(new Set())}
                    style={{ fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.text, cursor: 'pointer' }}>
                    Clear
                  </button>
                </div>
              </div>
              <div style={{ maxHeight: 380, overflowY: 'auto', marginBottom: 20 }}>
                {(() => {
                  // Group by category → section
                  const grouped = {};
                  articles.forEach(a => {
                    const cat = a.category_name || 'Articles';
                    const sec = a.section_name  || '';
                    if (!grouped[cat]) grouped[cat] = {};
                    if (!grouped[cat][sec]) grouped[cat][sec] = [];
                    grouped[cat][sec].push(a);
                  });
                  const multiCat = Object.keys(grouped).length > 1;
                  const multiSec = Object.values(grouped).some(s => Object.keys(s).filter(Boolean).length > 1);
                  const showGroups = multiCat || multiSec;

                  return Object.entries(grouped).map(([catName, sections]) => (
                    <div key={catName}>
                      {showGroups && multiCat && (
                        <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', padding: '10px 4px 4px', textTransform: 'uppercase' }}>
                          {catName}
                        </div>
                      )}
                      {Object.entries(sections).map(([secName, secArticles]) => {
                        const secIds = secArticles.map(a => String(a.id));
                        const allSel = secIds.every(id => selectedIds.has(id));
                        const someSel = !allSel && secIds.some(id => selectedIds.has(id));
                        const toggleSection = () => setSelectedIds(prev => {
                          const next = new Set(prev);
                          if (allSel) secIds.forEach(id => next.delete(id));
                          else secIds.forEach(id => next.add(id));
                          return next;
                        });
                        return (
                          <div key={secName || catName} style={{ marginBottom: 6 }}>
                            {showGroups && secName && (
                              <div onClick={toggleSection} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: C.elevated, borderRadius: '6px 6px 0 0', border: `1px solid ${C.border}`, cursor: 'pointer', userSelect: 'none' }}>
                                <div style={{ width: 14, height: 14, borderRadius: 3, flexShrink: 0, border: `2px solid ${allSel || someSel ? C.purple : C.border}`, background: allSel ? C.purple : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {allSel && <span style={{ color: 'white', fontSize: 9, fontWeight: 900 }}>✓</span>}
                                  {someSel && <span style={{ color: C.purpleHi, fontSize: 9, fontWeight: 900 }}>−</span>}
                                </div>
                                <span style={{ fontSize: 12, fontWeight: 600, color: C.text, flex: 1 }}>{secName}</span>
                                <span style={{ fontSize: 10, color: C.muted }}>{secIds.filter(id => selectedIds.has(id)).length}/{secArticles.length}</span>
                              </div>
                            )}
                            <div style={{ border: showGroups && secName ? `1px solid ${C.border}` : 'none', borderTop: 'none', borderRadius: showGroups && secName ? '0 0 6px 6px' : 6 }}>
                              {secArticles.map((article, idx) => {
                                const sel = selectedIds.has(String(article.id));
                                const preview = (article.body_text || article.body || '').substring(0, 110);
                                return (
                                  <div key={article.id} onClick={() => toggleArticle(article.id)}
                                    style={{ padding: '9px 12px', cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'flex-start', background: sel ? C.purpleLo : 'transparent', borderBottom: idx < secArticles.length - 1 ? `1px solid ${C.border}20` : 'none', transition: 'background 0.1s' }}>
                                    <div style={{ width: 14, height: 14, borderRadius: 3, flexShrink: 0, marginTop: 2, border: `2px solid ${sel ? C.purple : C.border}`, background: sel ? C.purple : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      {sel && <span style={{ color: 'white', fontSize: 9, fontWeight: 900 }}>✓</span>}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 2 }}>{article.title}</div>
                                      {preview && <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{preview}{preview.length === 110 ? '…' : ''}</div>}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ));
                })()}
              </div>
            </>
          )}

          {/* Agent prompt override */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
              AGENT PROMPT OVERRIDE <span style={{ fontWeight: 400 }}>(optional)</span>
            </label>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
              Override the active Agent Config's prompt for ticket tests and benchmark runs.
              Leave blank to use the active Agent Config's system prompt.
            </div>
            <textarea
              value={agentPrompt}
              onChange={e => setAgentPrompt(e.target.value)}
              rows={4}
              placeholder="Paste a custom agent system prompt here, or leave blank..."
              style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.elevated, color: C.text, fontSize: 12, outline: 'none', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.6, fontFamily: 'ui-monospace, monospace' }}
            />
            {agentPrompt.length > 0 && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{agentPrompt.length} characters</div>}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button onClick={handleClear} style={{ fontSize: 12, color: C.muted, background: 'transparent', border: 'none', cursor: 'pointer', padding: '0 4px' }}>
              Clear all selections
            </button>
            <button onClick={handleSave}
              style={{ padding: '9px 22px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: saved ? C.green : 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: 'white', transition: 'background 0.2s' }}>
              {saved ? '✓ Saved' : '💾 Apply to All Tests'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

export default function AgentConfigTab({ theme, triggerNewConfig, onActiveConfigChange, onKbChange, sharedKbArticles }) {
  const isDark = theme !== 'light';

  const { authFetch } = useApi();
  const [configs, setConfigs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  // Editor
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);

  // Run
  const [runModalConfig, setRunModalConfig] = useState(null);
  const [runningId, setRunningId] = useState(null);
  const [runProgress, setRunProgress] = useState(0);
  const [runProgressMsg, setRunProgressMsg] = useState('');
  const [runError, setRunError] = useState('');

  // Results
  const [results, setResults] = useState(null);

  // Tips dismiss
  const [tipsDismissed, setTipsDismissed] = useState(
    () => localStorage.getItem('sandbox_tips_dismissed') === 'true'
  );
  const dismissTips = () => {
    localStorage.setItem('sandbox_tips_dismissed', 'true');
    setTipsDismissed(true);
  };

  // ── Propagate selected config to parent (App.js → ScenariosTab) ──
  useEffect(() => {
    if (!onActiveConfigChange) return;
    const active = configs.find(c => c.id === selectedId) || null;
    onActiveConfigChange(active);
  }, [selectedId, configs, onActiveConfigChange]);

  const loadConfigs = useCallback(async () => {
    try {
      const res = await authFetch('/api/agent-configs');
      setConfigs(res.configs || []);
    } catch {}
    finally { setLoading(false); }
  }, [authFetch]);

  const loadTickets = useCallback(async () => {
    setLoadingTickets(true);
    try {
      const res = await authFetch('/api/zendesk/tickets?limit=200');
      setTickets(res.tickets || []);
    } catch {}
    finally { setLoadingTickets(false); }
  }, [authFetch]);

  useEffect(() => {
    loadConfigs();
    loadTickets();
  }, [loadConfigs, loadTickets]);

  // Respond to parent's "+ New Config" button in top header
  useEffect(() => {
    if (triggerNewConfig > 0) {
      setEditingConfig(null);
      setEditorOpen(true);
    }
  }, [triggerNewConfig]);

  // Auto-select first config so Agent Preview isn't empty on load
  useEffect(() => {
    if (configs.length > 0 && !selectedId) {
      const active = configs.find(c => c.is_active) || configs[0];
      setSelectedId(active.id);
    }
  }, [configs, selectedId]);

  // ── CRUD ──────────────────────────────────────────────────────────
  const handleSave = async (form) => {
    if (editingConfig?.id) {
      await authFetch(`/api/agent-configs/${editingConfig.id}`, { method: 'PUT', body: JSON.stringify(form) });
    } else {
      await authFetch('/api/agent-configs', { method: 'POST', body: JSON.stringify(form) });
    }
    setEditorOpen(false);
    setEditingConfig(null);
    await loadConfigs();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this config?')) return;
    await authFetch(`/api/agent-configs/${id}`, { method: 'DELETE' });
    if (selectedId === id) setSelectedId(null);
    await loadConfigs();
  };

  const handleDuplicate = async (id) => {
    await authFetch(`/api/agent-configs/${id}/duplicate`, { method: 'POST', body: JSON.stringify({}) });
    await loadConfigs();
  };

  const handleSetActive = async (id) => {
    await authFetch(`/api/agent-configs/${id}`, { method: 'PUT', body: JSON.stringify({ is_active: true }) });
    await loadConfigs();
  };

  // ── Run simulation ────────────────────────────────────────────────
  const handleRun = async ({ ticket_ids, max_tickets }) => {
    setRunModalConfig(null);
    setRunningId(runModalConfig?.id);
    setRunError('');
    setRunProgress(0);
    setRunProgressMsg('Starting simulation...');
    setResults(null);

    const msgs = [
      'Preparing tickets...',
      'Running AI conversations...',
      'Evaluating responses...',
      'Calculating scores...',
      'Finalizing results...',
    ];

    const start = Date.now();
    const iv = setInterval(() => {
      const p = Math.min(88, Math.round(40 * Math.log10((Date.now() - start) / 1000 + 1)));
      setRunProgress(p);
      setRunProgressMsg(msgs[Math.floor((p / 88) * (msgs.length - 1))]);
    }, 1200);

    try {
      const res = await authFetch(`/api/agent-configs/${runModalConfig.id}/run`, {
        method: 'POST',
        body: JSON.stringify({
          ticket_ids: ticket_ids || null,
          max_tickets: max_tickets || 10,
        }),
      });
      clearInterval(iv);
      setRunProgress(100);
      setRunProgressMsg('✓ Simulation complete!');
      setTimeout(() => {
        setResults({ data: res, configName: runModalConfig.name });
        setRunProgress(0);
        setRunProgressMsg('');
        setRunningId(null);
      }, 600);
    } catch (e) {
      clearInterval(iv);
      setRunError(e.message || 'Simulation failed');
      setRunProgress(0);
      setRunProgressMsg('');
      setRunningId(null);
    }
  };

  const openRunModal = (config) => {
    setRunModalConfig(config);
    loadTickets();
  };

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div style={{ background: isDark ? C.bg : '#f8fafc', minHeight: '100vh', color: C.text, fontFamily: 'system-ui, sans-serif' }}>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 28px', display: 'flex', gap: 24, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>

        {/* ── What is an Agent Config? explainer ── */}
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 12, padding: '18px 20px', marginBottom: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, flexShrink: 0,
              background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
            }}>🤖</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 5 }}>
                Your Agent Config = The Agent Under Test
              </div>
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, marginBottom: 12 }}>
                Each config defines <strong style={{ color: C.text }}>exactly how your AI agent thinks and responds</strong> —
                its <strong style={{ color: C.text }}>system prompt</strong> (persona, tone, escalation rules) and
                its <strong style={{ color: C.text }}>knowledge base</strong> (your Help Center content).
                The config marked <strong style={{ color: C.green }}>Active</strong> is the agent that runs in every scenario and ticket test.
                To test a different agent — different prompt, different KB scope — duplicate a config, edit it, and set it as active.
                Use <strong style={{ color: C.text }}>Global KB Configuration</strong> below to apply KB articles across all tabs.
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {[
                  { icon: '📝', label: 'System Prompt', desc: 'Defines the agent\'s persona & rules' },
                  { icon: '📚', label: 'Knowledge Base', desc: 'Grounds responses in your Help Center' },
                  { icon: '⎘',  label: 'A/B Testing',   desc: 'Duplicate → edit → compare results' },
                ].map(({ icon, label, desc }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14 }}>{icon}</span>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{label}</div>
                      <div style={{ fontSize: 10, color: C.muted }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        {runningId && (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18, marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: C.text }}>{runProgressMsg}</span>
              <span style={{ fontSize: 13, color: C.purpleHi, fontWeight: 700 }}>{runProgress}%</span>
            </div>
            <div style={{ height: 8, background: C.border, borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${runProgress}%`, background: 'linear-gradient(90deg,#7c3aed,#a78bfa)', borderRadius: 4, transition: 'width 0.4s ease' }} />
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>⏱ Running AI simulations + evaluation — this takes ~30–90s depending on ticket count</div>
          </div>
        )}

        {/* Error */}
        {runError && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, color: C.red, fontSize: 13 }}>
            ⚠️ {runError}
          </div>
        )}

        {/* Results panel */}
        {results && (
          <div style={{ marginBottom: 24 }}>
            <SimulationResults
              data={results.data}
              configName={results.configName}
              onClose={() => setResults(null)}
            />
          </div>
        )}

        {/* Config list */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>Loading configurations...</div>
        ) : configs.length === 0 ? (
          <EmptyState
            icon="🤖"
            title="No agent configs yet"
            subtitle="Create your first agent configuration to define how your AI agent should behave and what it knows."
            action={
              <button onClick={() => { setEditingConfig(null); setEditorOpen(true); }}
                style={{ padding: '10px 22px', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                + Create First Config
              </button>
            }
          />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: 12 }}>
            {configs.map((cfg, i) => (
              <div
                key={cfg.id}
                style={{
                  animation: 'cardEntrance 0.3s ease-out both',
                  animationDelay: `${Math.min(i * 50, 400)}ms`,
                }}
              >
                <ConfigCard
                  config={cfg}
                  selected={selectedId === cfg.id}
                  onSelect={setSelectedId}
                  onEdit={(c) => { setEditingConfig(c); setEditorOpen(true); }}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDelete}
                  onRun={openRunModal}
                  onSetActive={handleSetActive}
                  running={runningId === cfg.id}
                />
              </div>
            ))}
          </div>
        )}

        {/* ── Global KB Configuration section ── */}
        {onKbChange && (
          <GlobalKbSection
            onKbChange={onKbChange}
            sharedKbArticles={sharedKbArticles}
          />
        )}

        {/* Tips */}
        {configs.length > 0 && !results && !runningId && !tipsDismissed && (
          <div style={{ marginTop: 28, padding: '16px 20px', background: C.surface, borderRadius: 12, border: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, letterSpacing: '0.06em' }}>💡 TIPS</div>
              <button
                onClick={dismissTips}
                title="Dismiss tips"
                style={{ background: 'transparent', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px' }}
              >×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
              {[
                ['⎘ Duplicate for A/B testing', 'Copy a config, tweak the prompt, run both, compare containment rates.'],
                ['📚 Add a knowledge base', 'Paste your FAQ or help docs — the agent will cite from it, reducing hallucinations.'],
                ['🌡 Tune temperature', 'Lower temp = consistent, safe. Higher = creative but riskier. Start at 0.3.'],
                ['🔄 More turns = higher chance of resolution', 'Increase max turns for complex ticket types like billing disputes.'],
              ].map(([title, desc]) => (
                <div key={title}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 3 }}>{title}</div>
                  <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{desc}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        </div>{/* end left column */}

        {/* ── Right sidebar: Agent Preview ── */}
        {(() => {
          const activeConfig = configs.find(c => c.id === selectedId);
          const kbLines = activeConfig?.knowledge_base ? activeConfig.knowledge_base.split('\n').filter(l => l.trim()).length : 0;
          return (
            <div style={{
              width: 280, flexShrink: 0, position: 'sticky', top: 72,
              display: 'flex', flexDirection: 'column', gap: 16,
            }}>
              {/* Agent Preview card */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', marginBottom: 14 }}>AGENT PREVIEW</div>
                {activeConfig ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#10b981', boxShadow: '0 0 6px #10b981' }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Agent Online</span>
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 14 }}>
                      Configured with <strong style={{ color: C.text }}>{kbLines} KB lines</strong> and temperature <strong style={{ color: C.text }}>{activeConfig.temperature}</strong>
                    </div>
                    <div style={{ fontSize: 11, color: C.muted, background: C.elevated, borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontFamily: 'monospace', maxHeight: 80, overflow: 'hidden', opacity: 0.8 }}>
                      {activeConfig.system_prompt.slice(0, 120)}{activeConfig.system_prompt.length > 120 ? '...' : ''}
                    </div>
                    <button
                      onClick={() => openRunModal(activeConfig)}
                      style={{ width: '100%', padding: '9px 0', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      ▶ Test in Sandbox
                    </button>
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: C.muted, textAlign: 'center', padding: '20px 0' }}>
                    Click a config card to preview it here
                  </div>
                )}
              </div>

              {/* Security & Privacy card */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', marginBottom: 14 }}>SECURITY & PRIVACY</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: C.muted }}>PII Anonymization</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '2px 8px', borderRadius: 20 }}>ON</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: C.muted }}>Workspace Isolation</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '2px 8px', borderRadius: 20 }}>ACTIVE</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>{/* end flex row */}

      {/* Modals */}
      {editorOpen && (
        <ConfigEditor
          config={editingConfig}
          onSave={handleSave}
          onCancel={() => { setEditorOpen(false); setEditingConfig(null); }}
        />
      )}

      {runModalConfig && (
        <RunSettingsModal
          config={runModalConfig}
          tickets={tickets}
          loadingTickets={loadingTickets}
          onRun={handleRun}
          onCancel={() => setRunModalConfig(null)}
        />
      )}
    </div>
  );
}
